import React from 'react';
import { styled } from '@this/constants/themes';
import { media } from '@this/components/shared/atoms/media';
import type { LegSummaryJson } from '@this/src/domain/select_repository';
import SegmentClass from '@this/src/domain/transit/segment';
import IndividualRouteModal from './IndividualRouteModal';
import TravelModeIcon from '../shared/TravelModeIcon';
import { TransitCompanyImage } from '../../shared/TransitCompanyImage';

interface IndividualRouteInfoProps {
  legSummary: LegSummaryJson;
  segment: SegmentClass;
  isTrainOrFlight: boolean;
  omitOuterPadding?: boolean;
}

const IndividualRouteInfo: React.FC<IndividualRouteInfoProps> = ({
  legSummary,
  segment,
  isTrainOrFlight,
  omitOuterPadding = false
}) => {
  const companyImagePath = SegmentClass.imagePathByName(legSummary.name);

  return (
    <StyledSummary $omitOuterPadding={omitOuterPadding}>
      {legSummary.from.express_station && legSummary.from.express_station !== legSummary.from.name && (
        <StyledBoxContentThin $isTrainOrFlight={isTrainOrFlight}>
          <StyledLegTime $isTrainOrFlight={isTrainOrFlight} />
          <div>乗車駅: {legSummary.from.name}</div>
        </StyledBoxContentThin>
      )}
      <StyledBoxContent>
        <StyledLegTime $isTrainOrFlight={isTrainOrFlight}>{legSummary.from.time}</StyledLegTime>
        <StyledBoxTitle $isTrainOrFlight={isTrainOrFlight}>出発</StyledBoxTitle>
        <StyledSpace
          className={isTrainOrFlight ? 'select-transport-box__button_exist_leg_station' : ''}
          $isTrainOrFlight={isTrainOrFlight}
          $growOnMobile
        >
          <StyledName $isTrainOrFlight={isTrainOrFlight}>
            {legSummary.from.express_station || legSummary.from.name}
          </StyledName>
        </StyledSpace>
      </StyledBoxContent>
      <StyledBoxContent>
        <StyledMiddleRowLead>
          <StyledLegTimeSpace $isTrainOrFlight={isTrainOrFlight} className="domestic-transport__leg-time-space" />
          <TravelModeIcon
            type={legSummary.type}
            legSummary={legSummary}
            segment={segment}
            muted={!isTrainOrFlight}
          />
        </StyledMiddleRowLead>
        <StyledMiddleRowBody>
          <StyledRouteMetaText $isTrainOrFlight={isTrainOrFlight}>
            {legSummary.name?.match(/\//) ? (
              legSummary.name.split('/').map((n: string, index: number) => {
                const imagePath = SegmentClass.imagePathByName(n);
                return (
                  <React.Fragment key={`transport_${n}`}>
                    {index > 0 && ' / '}
                    {imagePath && <TransitCompanyImage src={imagePath} alt="" />}
                    {n}
                  </React.Fragment>
                );
              })
            ) : (
              <>
                {companyImagePath && <TransitCompanyImage src={companyImagePath} alt="" />}
                {legSummary.name}
              </>
            )}
          </StyledRouteMetaText>
          <IndividualRouteModal segment={segment} legSummary={legSummary} />
        </StyledMiddleRowBody>
      </StyledBoxContent>
      <StyledBoxContent>
        <StyledLegTime $isTrainOrFlight={isTrainOrFlight}>{legSummary.to.time}</StyledLegTime>
        <StyledBoxTitle $isTrainOrFlight={isTrainOrFlight}>到着</StyledBoxTitle>
        <StyledSpace
          className={isTrainOrFlight ? 'select-transport-box__button_exist_leg_station' : ''}
          $isTrainOrFlight={isTrainOrFlight}
          $growOnMobile
        >
          <StyledName $isTrainOrFlight={isTrainOrFlight}>
            {legSummary.to.express_station || legSummary.to.name}
          </StyledName>
        </StyledSpace>
      </StyledBoxContent>
      <StyledBoardingArrival className="select-transport-box__left__boarding-arrival">
        {legSummary.to.express_station && legSummary.to.express_station !== legSummary.to.name && (
          <StyledBoxContentThin $isTrainOrFlight={isTrainOrFlight}>
            <StyledLegTime $isTrainOrFlight={isTrainOrFlight} />
            <div>降車駅: {legSummary.to.name}</div>
          </StyledBoxContentThin>
        )}
      </StyledBoardingArrival>
    </StyledSummary>
  );
};

const StyledSummary = styled.div<{ $omitOuterPadding?: boolean }>`
  width: 100%;
  padding: ${props => (props.$omitOuterPadding ? '0' : '8px 5px 8px 10px')};

  ${props =>
    media.spSmall`
      padding: ${props.$omitOuterPadding ? '0' : '10px'};
    `}
`;

const StyledName = styled.div<{ $isTrainOrFlight: boolean }>`
  font-size: 16px;
  font-weight: bold;
  color: ${props => (props.$isTrainOrFlight ? 'inherit' : '#727272')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;

  ${media.spSmall`
    word-break: break-word;
    white-space: normal;
  `}
`;

const StyledLegTime = styled.div<{ $isTrainOrFlight: boolean }>`
  min-width: 50px;
  color: ${props => (props.$isTrainOrFlight ? '#3675ff' : '#999999')};
  font-size: 13px;
  height: 16px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: left;
  flex-shrink: 0;
  margin-right: 10px;

  ${media.spSmall`
    margin-right: 0;
    height: auto;
    padding-top: 2px;
  `}
`;

const StyledLegTimeSpace = styled.div<{ $isTrainOrFlight: boolean }>`
  min-width: 50px;
  color: ${props => (props.$isTrainOrFlight ? '#3675ff' : '#999999')};
  font-size: 13px;
  height: 16px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: left;
  flex-shrink: 0;
  margin-right: 10px;

  ${media.spSmall`
    margin-right: 0;
  `}
`;

const StyledBoxTitle = styled.div<{ $isTrainOrFlight: boolean }>`
  background: ${props => (props.$isTrainOrFlight ? '#aaa' : '#cccccc')};
  color: white;
  margin-right: 10px;
  width: 40px;
  height: 20px;
  font-size: 12px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const StyledBoxContent = styled.div`
  display: flex;
  align-items: center;
  height: 2rem;

  ${media.spSmall`
    align-items: flex-start;
    height: auto;
    min-height: 2rem;
    padding-top: 2px;
    padding-bottom: 2px;
  `}
`;

const StyledBoxContentThin = styled.div<{ $isTrainOrFlight: boolean }>`
  display: flex;
  padding: 2px;
  color: ${props => (props.$isTrainOrFlight ? 'inherit' : '#727272')};
`;

const StyledMiddleRowLead = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  ${media.spSmall`
    align-items: flex-start;
  `}
`;

const StyledMiddleRowBody = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;

  ${media.spSmall`
    align-items: flex-start;
  `}
`;

const StyledRouteMetaText = styled.span<{ $isTrainOrFlight: boolean }>`
  margin-right: 10px;
  font-size: 14px;
  color: ${props => (props.$isTrainOrFlight ? 'inherit' : '#727272')};
  min-width: 0;
  flex: 1;

  ${media.spSmall`
    line-height: 1.4;
    word-break: break-word;
  `}
`;

const StyledSpace = styled.div<{ $isTrainOrFlight: boolean; $growOnMobile?: boolean }>`
  margin-right: 10px;
  font-size: 14px;
  color: ${props => (props.$isTrainOrFlight ? 'inherit' : '#727272')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 400px;

  ${props =>
    media.spSmall`
      ${props.$growOnMobile ? 'flex: 1; min-width: 0;' : ''}
      word-break: break-word;
      white-space: normal;
      line-height: 1.4;
    `}
`;

const StyledBoardingArrival = styled.div`
  font-size: 14px;
`;

export default IndividualRouteInfo;
